import React from 'react';
import styled, { useTheme } from 'styled-components';
import Markdown from 'markdown-to-jsx';
import { useStory } from '../storyblok/hooks/useStoryblok';
import { storyblokEditable } from '@storyblok/react';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { mediaQuery, mediaQueryMin, typography } from '../../theme';
import { RouterLink } from '../snackbar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMediaQuery } from 'react-responsive';
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary as AccordionSummary,
} from '../accordion/Accordion';
import { SbComponentBase } from '../storyblok/types';
import dayjs from 'dayjs';
import { Path } from '../../routes/components/path';
import { usePathname } from 'next/navigation';
import { colors } from '../../theme/color';

const tiktokIcon = 'https://mob-cdn.azureedge.net/images/tiktok.svg';

export type FooterCategory = SbComponentBase & {
  content: string;
};

//Todo: Remove these 2 lines afterwards
const isTodayHomelessDate = dayjs().isSame('2025-10-17', 'day');
const homelessPizzaLogo = 'https://mob-cdn.azureedge.net/images/Koditonpizza_logo_noPadding.png';

export const hiddenFooterPaths = [Path.AppWebViewOrderPayment, Path.PaymentRedirect];

const minimalFooterPaths = [Path.Order, Path.Shoppingcart];

export type FooterData = SbComponentBase & {
  footer_categories?: FooterCategory[];
  footer_bottom?: FooterCategory[];
  footer_simple?: FooterCategory[];
};

export const Footer: React.FC = () => {
  const { data: footerData } = useStory<FooterData>('footer', 'footer');
  const isMobile = useMediaQuery({ query: mediaQuery.large });
  const theme = useTheme();
  const pathname = usePathname();
  const minimal = minimalFooterPaths.some((path) => pathname.includes(path));

  return (
    <FooterContainer data-testid="footer" id="main-footer">
      <WidthContainer>
        <LogoContainer minimal={minimal}>
          {isTodayHomelessDate ? (
            <StyledLogo src={homelessPizzaLogo} alt="Koditon pizza" />
          ) : (
            <StyledLogo src={theme?.logo.white} alt="Kotipizza" />
          )}
          {minimal && (
            <>
              {footerData?.footer_simple?.map((footerCategory) => (
                <MinimalFooterCategory
                  {...storyblokEditable(footerCategory)}
                  key={footerCategory._uid}
                >
                  <Markdown
                    options={{
                      overrides: {
                        a: {
                          component: RouterLink,
                        },
                      },
                    }}
                  >
                    {footerCategory.content}
                  </Markdown>
                </MinimalFooterCategory>
              ))}
            </>
          )}
        </LogoContainer>
        {!minimal && (
          <FlexContainer>
            {footerData?.footer_categories?.map((footerCategory) => (
              <FooterCategory {...storyblokEditable(footerCategory)} key={footerCategory._uid}>
                {isMobile ? (
                  <StyledAccordion key={footerCategory._uid}>
                    <StyledAccordionSummary
                      expandIcon={<ExpandMoreIcon htmlColor={colors.white} />}
                      aria-controls={`question-${footerCategory._uid}-content`}
                      id={`question-${footerCategory._uid}-header`}
                    >
                      <Markdown
                        options={{
                          overrides: {
                            p: {
                              component: () => null,
                            },
                          },
                        }}
                      >
                        {footerCategory.content}
                      </Markdown>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails id={`question-${footerCategory._uid}-content`}>
                      <Markdown
                        options={{
                          overrides: {
                            h2: {
                              component: () => null,
                            },
                            h5: {
                              component: () => null,
                            },
                            a: {
                              component: RouterLink,
                            },
                          },
                        }}
                      >
                        {footerCategory.content}
                      </Markdown>
                    </StyledAccordionDetails>
                  </StyledAccordion>
                ) : (
                  <Markdown
                    options={{
                      overrides: {
                        a: {
                          component: RouterLink,
                        },
                      },
                    }}
                  >
                    {footerCategory.content}
                  </Markdown>
                )}
              </FooterCategory>
            ))}
            <SocialMediaFooterCategory>
              <h2>Seuraa meitä</h2>
              <div>
                <p>
                  <a href="https://www.tiktok.com/@Kotipizza" target="_blank" rel="noreferrer">
                    <img alt="Tiktok logo" aria-hidden="true" src={tiktokIcon} />
                    <span>TikTok</span>
                  </a>
                </p>
                <p>
                  <a href="https://www.facebook.com/kotipizza" target="_blank" rel="noreferrer">
                    <FacebookRoundedIcon />
                    <span>Facebook</span>
                  </a>
                </p>
                <p>
                  <a href="https://www.instagram.com/kotipizza/" target="_blank" rel="noreferrer">
                    <InstagramIcon />
                    <span>Instagram</span>
                  </a>
                </p>
                <p>
                  <a
                    href="https://www.youtube.com/user/KotipizzaOfficial"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <YouTubeIcon />
                    <span>YouTube</span>
                  </a>
                </p>
              </div>
            </SocialMediaFooterCategory>
          </FlexContainer>
        )}
      </WidthContainer>
      {footerData?.footer_bottom && (
        <FooterBottom {...storyblokEditable(footerData?.footer_bottom[0])}>
          <Markdown
            options={{
              wrapper: React.Fragment,
              overrides: {
                a: {
                  component: RouterLink,
                },
              },
            }}
          >
            {footerData?.footer_bottom[0]?.content || ''}
          </Markdown>
          <CookiebotRenewLink onClick={() => window.Cookiebot?.renew()}>
            Muuta evästeasetuksiasi
          </CookiebotRenewLink>
        </FooterBottom>
      )}
    </FooterContainer>
  );
};

const FooterContainer = styled.div`
  ${typography.textStyle}
  width: 100%;
  background-color: ${colors.green.darker(75)};
  color: ${colors.textWhite};
  z-index: 2;

  a,
  button {
    color: ${colors.textWhite};
    text-decoration: none;
    display: flex;
    align-items: center;
    border-radius: 4px;
    transition: all 0.1s ease-in;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid ${colors.white};
      outline-offset: 4px;
    }
  }
`;

const CookiebotRenewLink = styled.button`
  padding-left: 0;
`;

const WidthContainer = styled.div`
  padding: 0 64px;
  max-width: 1200px;
  margin: auto;

  @media ${mediaQuery.large} {
    padding: 0 24px;
  }

  @media ${mediaQuery.medium} {
    padding: 0 16px;
  }
`;

const StyledLogo = styled.img`
  width: 80px;
  margin: 0 8px;
`;

const FlexContainer = styled.div`
  width: 100%;

  @media ${mediaQueryMin.large} {
    margin-top: 32px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const FooterCategory = styled.div`
  padding: 20px 0;
  flex-grow: 1;

  & p {
    margin: 1rem 0;
  }

  h2,
  h5 {
    ${typography.headingBrandSmallStyle}
  }

  @media ${mediaQuery.large} {
    flex-basis: 220px;
  }
`;

const SocialMediaFooterCategory = styled(FooterCategory)`
  @media ${mediaQuery.large} {
    h2,
    h5 {
      ${typography.overlineStyle}
      text-align: center;
    }

    & > div {
      display: flex;
      justify-content: space-between;
      margin: 0 32px;

      & span {
        clip: rect(0 0 0 0);
        width: 1px;
        height: 1px;
        margin: -1;
        overflow: hidden;
      }
    }
  }

  svg,
  img {
    margin-right: 8px;
  }
`;

const FooterBottom = styled.div`
  padding: 32px 64px;
  max-width: 1200px;
  margin: auto;
  border-top: 1px solid ${colors.white};
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;

  p,
  button {
    ${typography.textSmallStyle}
    margin: 8px 24px 8px 0;
    word-wrap: none;
    background: none;
    border: none;
  }

  @media ${mediaQuery.large} {
    padding: 24px;
  }

  @media ${mediaQuery.medium} {
    padding: 16px;
  }
`;

const LogoContainer = styled.div<{ minimal: boolean }>`
  display: flex;
  align-items: center;
  margin: 0 -8px;
  gap: 16px;
  margin: ${(props) => (props.minimal ? '24px 0' : '40px 0')};

  @media ${mediaQuery.large} {
    justify-content: ${(props) => (props.minimal ? 'flex-start' : 'center')};
    margin: 24px 0;
    gap: 0;
  }
`;

const MinimalFooterCategory = styled(FooterCategory)`
  flex-grow: 0;
  flex-basis: auto;
  padding: 8px;

  p,
  a {
    ${typography.textLargeStyle}
  }

  @media ${mediaQuery.xsmall} {
    display: block;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  text-align: left;

  & > div {
    align-items: flex-start;
    color: white;
  }
`;
